// src/pages/ProductDetail.js
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';

const ProductDetail = () => {
  const { barcode } = useParams();
  const [products, setProducts] = useState([]); // لیست محصولات (ممکن است چندتا باشد)
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedMonths, setSelectedMonths] = useState(6);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, settingsRes] = await Promise.all([
          axios.get(`/products/${barcode}`),
          axios.get('/settings'),
        ]);

        const data = Array.isArray(productsRes.data)
          ? productsRes.data
          : productsRes.data
          ? [productsRes.data]
          : [];

        setProducts(data);
        setSettings(settingsRes.data);
      } catch (error) {
        console.error('خطا در دریافت جزئیات:', error);
        alert('خطایی رخ داد');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [barcode]);

  const toToman = (value) => {
    if (value == null || isNaN(value)) return '۰';
    return Number(value).toLocaleString('fa-IR');
  };

  const formatPercent = (value) => {
    if (!value && value !== 0) return '۰.۰۰';
    return parseFloat(value).toFixed(2);
  };

  const addToCart = (product, paymentType) => {
    const cart = JSON.parse(localStorage.getItem('cart') || '[]');
    const exists = cart.some((item) => item.product.id === product.id);

    if (exists) {
      alert('این محصول قبلاً به سبد خرید اضافه شده است');
      return;
    }

    cart.push({
      product,
      quantity: 1,
      paymentType,
      months: paymentType === 'installment' ? selectedMonths : null,
    });

    localStorage.setItem('cart', JSON.stringify(cart));
    window.dispatchEvent(new Event('cartUpdated'));
    alert('محصول با موفقیت به سبد خرید اضافه شد!');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-2xl text-gold-dark animate-pulse">در حال بارگذاری...</div>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-2xl text-red-600">محصولی با این بارکد یافت نشد</div>
      </div>
    );
  }

  const prePaymentPercent = parseFloat(settings.installment_pre_payment_percent || 30);
  const maxMonths = parseInt(settings.installment_max_months || 12, 10);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gold-light/10 via-white to-gold-light/10 py-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Breadcrumb */}
        <div className="text-sm text-gray-600 mb-8">
          <Link to="/" className="hover:text-gold">خانه</Link> <span className="mx-2">/</span>
          <Link to="/products" className="hover:text-gold">محصولات</Link> <span className="mx-2">/</span>
          <span className="text-gold-dark">بارکد: {barcode} ({products.length} مورد)</span>
        </div>

        <h1 className="text-4xl md:text-5xl font-bold text-gold-dark text-center mb-12">
          محصولات با بارکد {barcode}
        </h1>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {products.map((product) => {
            const mainImg =
              product.images?.find((img) => img.is_main === 1) ||
              product.images?.[0];

            const cashPrice = product.final_price;
            const installmentPrice = product.price_before_discount;
            const prePayment = Math.round(installmentPrice * (prePaymentPercent / 100));
            const remainingValue = installmentPrice - prePayment;
            const remainingGoldWeight = remainingValue / (product.current_gold_price || 1);
            const monthlyGoldWeight =
              selectedMonths > 0 ? remainingGoldWeight / selectedMonths : 0;

            return (
              <div
                key={product.id}
                className="bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col"
              >
                {/* عکس محصول */}
                <div className="relative aspect-[3/4] overflow-hidden bg-gray-50">
                  <img
                    src={mainImg?.image_url || '/placeholder.jpg'}
                    alt={product.name}
                    className="absolute inset-0 w-full h-full object-contain p-6"
                  />
                  {product.mojood === 0 && (
                    <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                      <span className="text-white text-3xl font-bold drop-shadow-lg">
                        ناموجود
                      </span>
                    </div>
                  )}
                </div>

                {/* اطلاعات محصول */}
                <div className="p-6 flex flex-col flex-grow space-y-6">
                  <h2 className="text-2xl font-bold text-gold-dark line-clamp-2 min-h-[3rem]">
                    {product.name || 'محصول بدون نام'}
                  </h2>

                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">وزن:</span>{' '}
                      <span className="font-bold">{product.vazn} گرم</span>
                    </div>
                    <div>
                      <span className="text-gray-600">نوع:</span>{' '}
                      <span className="font-bold">{product.noe_kala || '—'}</span>
                    </div>
                    {product.darsad > 0 && (
                      <div>
                        <span className="text-gray-600">اجرت:</span>{' '}
                        <span className="font-bold">{formatPercent(product.darsad)}%</span>
                      </div>
                    )}
                  </div>

                  {/* قیمت نقدی */}
                  <div className="border-t border-gray-200 pt-4 text-center">
                    {product.price_before_discount > cashPrice && (
                      <p className="text-sm text-green-600 mb-1">
                        تخفیف {formatPercent(product.discount_percent)}%
                      </p>
                    )}
                    <p className="text-3xl font-bold text-gold-dark">
                      {toToman(cashPrice)} تومان
                    </p>
                  </div>

                  {/* جزئیات محاسبه قیمت - آکاردئونی */}
                  <details className="mt-2 border-t border-gray-200 pt-4">
                    <summary className="cursor-pointer text-gold-dark font-bold text-base hover:text-gold transition-colors duration-200">
                      نمایش جزئیات محاسبه قیمت
                    </summary>

                    <div className="mt-4 space-y-2.5 text-sm text-gray-700">
                      <div className="flex justify-between">
                        <span>هر گرم طلا:</span>
                        <span>{toToman(product.current_gold_price || 0)} تومان</span>
                      </div>

                      <div className="flex justify-between">
                        <span>طلای خام:</span>
                        <span>{toToman(product.raw_gold_price || 0)} تومان</span>
                      </div>

                      <div className="flex justify-between">
                        <span>اجرت ساخت ({formatPercent(product.darsad || 0)}%):</span>
                        <span>{toToman(product.ojrat_total || 0)} تومان</span>
                      </div>

                      <div className="flex justify-between">
                        <span>سود فروشگاه ({formatPercent(product.sood || product.profit_percent || 0)}%):</span>
                        <span>{toToman(product.effective_profit_amount || 0)} تومان</span>
                      </div>

                      <div className="flex justify-between">
                        <span>مالیات بر ارزش افزوده ({formatPercent(product.vat_percent || 0)}%):</span>
                        <span>{toToman(product.vat_amount || 0)} تومان</span>
                      </div>

                      <div className="flex justify-between font-bold border-t pt-3 mt-3">
                        <span>قیمت قبل از تخفیف:</span>
                        <span>{toToman(product.price_before_discount)} تومان</span>
                      </div>

                      {Number(product.discount_percent) > 0 && (
                        <div className="flex justify-between text-green-600 font-medium">
                          <span>تخفیف ({formatPercent(product.discount_percent)}%):</span>
                          <span>
                            -{toToman((product.price_before_discount || 0) - (product.final_price || 0))} تومان
                          </span>
                        </div>
                      )}

                      <div className="flex justify-between font-bold text-lg border-t pt-3 text-gold-dark">
                        <span>قیمت نهایی (نقدی):</span>
                        <span>{toToman(product.final_price)} تومان</span>
                      </div>
                    </div>
                  </details>

                  {/* دکمه‌های خرید */}
                  <div className="mt-auto space-y-4 pt-6">
                    <button
                      onClick={() => addToCart(product, 'cash')}
                      className="w-full bg-gold hover:bg-gold-dark text-white font-bold py-4 rounded-xl transition shadow-md"
                    >
                      خرید نقدی
                    </button>

                    <button
                      onClick={() => addToCart(product, 'installment')}
                      className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold py-4 rounded-xl transition shadow-md"
                    >
                      خرید اقساطی ({selectedMonths} قسط)
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* اطلاعات کلی اقساط (اختیاری) */}
        <div className="mt-16 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-3xl p-8 border border-purple-200 max-w-2xl mx-auto text-center">
          <h3 className="text-2xl font-bold text-purple-900 mb-6">
            اطلاعات خرید اقساطی
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <p className="text-gray-600">پیش‌پرداخت</p>
              <p className="text-2xl font-bold text-purple-800">
                {prePaymentPercent}% مبلغ پایه
              </p>
            </div>
            <div>
              <p className="text-gray-600">حداکثر اقساط</p>
              <p className="text-2xl font-bold text-purple-800">{maxMonths} ماه</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;